import React, { useCallback, useEffect, useState } from 'react';
import {
  ShieldCheck, ShieldOff, Loader2, KeyRound, Copy, Download,
  AlertTriangle, CheckCircle2, RefreshCw,
} from 'lucide-react';
import { toast } from 'sonner';
import { api } from '../../App';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import {
  AlertDialog, AlertDialogContent, AlertDialogHeader, AlertDialogTitle,
  AlertDialogDescription, AlertDialogFooter, AlertDialogAction, AlertDialogCancel,
} from '../ui/alert-dialog';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';

function BackupCodesView({ codes, onClose, onConfirmSaved }) {
  const [acknowledged, setAcknowledged] = useState(false);
  const [copied, setCopied] = useState(false);

  const handleCopyAll = async () => {
    try {
      await navigator.clipboard.writeText(codes.join('\n'));
      setCopied(true);
      toast.success('Backup codes copied');
      setTimeout(() => setCopied(false), 2000);
    } catch {
      toast.error('Could not copy, please save them manually');
    }
  };

  const handleDownload = () => {
    const content = [
      'Zuriya, Two-factor backup codes',
      `Generated: ${new Date().toISOString()}`,
      '',
      'Each code can be used ONCE.',
      'Keep this file somewhere safe, ideally offline.',
      '',
      ...codes,
    ].join('\n');
    const blob = new Blob([content], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `zuriya_backup_codes_${new Date().toISOString().slice(0, 10)}.txt`;
    document.body.appendChild(link);
    link.click();
    link.remove();
    URL.revokeObjectURL(url);
  };

  return (
    <Dialog open onOpenChange={() => acknowledged && onClose()}>
      <DialogContent className="max-w-md" data-testid="backup-codes-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <KeyRound className="h-5 w-5 text-[#D4AF37]" />
            Save your backup codes
          </DialogTitle>
        </DialogHeader>
        <p className="text-sm text-stone-600 leading-relaxed">
          These codes are your safety net if you lose access to your email. <strong>Each works once.</strong> Save or download them now, we can't show them again.
        </p>
        <div className="grid grid-cols-2 gap-2 font-mono text-sm bg-[#F9F6F0] border border-[#E6E4DD] rounded-lg p-4 my-4">
          {codes.map((code) => (
            <div key={code} className="py-1 px-2 bg-white rounded border border-[#E6E4DD] text-center" data-testid="backup-code-item">
              {code}
            </div>
          ))}
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={handleCopyAll} className="flex-1 gap-1" data-testid="copy-backup-codes">
            {copied ? <CheckCircle2 className="h-4 w-4 text-emerald-500" /> : <Copy className="h-4 w-4" />}
            {copied ? 'Copied' : 'Copy all'}
          </Button>
          <Button variant="outline" onClick={handleDownload} className="flex-1 gap-1" data-testid="download-backup-codes">
            <Download className="h-4 w-4" />Download .txt
          </Button>
        </div>
        <label className="flex items-start gap-2 mt-4 text-xs text-stone-600 cursor-pointer">
          <input
            type="checkbox"
            checked={acknowledged}
            onChange={(e) => setAcknowledged(e.target.checked)}
            className="mt-0.5"
            data-testid="acknowledge-backup-codes"
          />
          <span>I've saved these somewhere safe and understand each is single-use.</span>
        </label>
        <Button
          disabled={!acknowledged}
          onClick={() => { onConfirmSaved?.(); onClose(); }}
          className="w-full mt-3 bg-[#1A1A1A] hover:bg-black text-white"
          data-testid="backup-codes-done"
        >
          Done
        </Button>
      </DialogContent>
    </Dialog>
  );
}

export default function TwoFactorSettingsCard() {
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [action, setAction] = useState(null); // 'enable' | 'disable' | 'regenerate' | null
  const [setupToken, setSetupToken] = useState(null);
  const [setupCode, setSetupCode] = useState('');
  const [disableCode, setDisableCode] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [newCodes, setNewCodes] = useState(null);

  const load = useCallback(async () => {
    try {
      const res = await api.get('/auth/2fa/status');
      setStatus(res.data);
    } catch (err) {
      console.error('2FA status fetch failed', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const startEnable = async () => {
    setSubmitting(true);
    try {
      const res = await api.post('/auth/2fa/setup/begin', { method: 'email' });
      setSetupToken(res.data.setup_token);
      setAction('enable');
      toast.success('Check your email for a 6-digit code');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not start setup');
    } finally {
      setSubmitting(false);
    }
  };

  const confirmEnable = async () => {
    if (setupCode.length !== 6) return;
    setSubmitting(true);
    try {
      const res = await api.post('/auth/2fa/setup/confirm', {
        setup_token: setupToken, code: setupCode,
      });
      setNewCodes(res.data.backup_codes);
      setAction(null);
      setSetupCode('');
      setSetupToken(null);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Incorrect code');
    } finally {
      setSubmitting(false);
    }
  };

  const startDisable = async () => {
    setSubmitting(true);
    try {
      await api.post('/auth/2fa/disable', {});
    } catch (err) {
      if (err.response?.status === 428) {
        toast.success('Check your email for a 6-digit code');
        setAction('disable');
      } else {
        toast.error(err.response?.data?.detail || 'Could not send code');
      }
    } finally {
      setSubmitting(false);
    }
  };

  const confirmDisable = async () => {
    if (disableCode.length !== 6) return;
    setSubmitting(true);
    try {
      await api.post('/auth/2fa/disable', { code: disableCode });
      toast.success('Two-factor authentication disabled');
      setAction(null);
      setDisableCode('');
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Incorrect code');
    } finally {
      setSubmitting(false);
    }
  };

  const startRegenerate = async () => {
    setSubmitting(true);
    try {
      await api.post('/auth/2fa/backup-codes/regenerate', {});
    } catch (err) {
      if (err.response?.status === 428) {
        toast.success('Check your email for a 6-digit code');
        setAction('regenerate');
      } else {
        toast.error(err.response?.data?.detail || 'Could not send code');
      }
    } finally {
      setSubmitting(false);
    }
  };

  const confirmRegenerate = async () => {
    if (setupCode.length !== 6) return;
    setSubmitting(true);
    try {
      const res = await api.post('/auth/2fa/backup-codes/regenerate', { code: setupCode });
      setNewCodes(res.data.backup_codes);
      setAction(null);
      setSetupCode('');
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Incorrect code');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="card-surface p-4 sm:p-6 flex items-center justify-center">
        <Loader2 className="h-5 w-5 animate-spin text-stone-400" />
      </div>
    );
  }

  const { enabled, backup_codes_remaining, required_by_role } = status || {};

  return (
    <>
      <div className="card-surface p-4 sm:p-6" data-testid="two-factor-settings-card">
        <div className="flex items-start gap-3">
          <div className={`w-10 h-10 rounded-full flex items-center justify-center shrink-0 ${enabled ? 'bg-emerald-50 border border-emerald-200' : 'bg-stone-100 border border-stone-200'}`}>
            {enabled
              ? <ShieldCheck className="h-5 w-5 text-emerald-600" />
              : <ShieldOff className="h-5 w-5 text-stone-500" />}
          </div>
          <div className="flex-1">
            <div className="flex items-center gap-2 flex-wrap mb-1">
              <h3 className="font-heading text-lg font-medium text-[#1A1A1A]">Two-factor authentication</h3>
              {enabled ? (
                <span className="inline-flex items-center gap-1 text-[11px] font-medium bg-emerald-100 text-emerald-700 px-2 py-0.5 rounded-full" data-testid="tfa-badge-on">
                  <CheckCircle2 className="h-3 w-3" />On
                </span>
              ) : (
                <span className="inline-flex items-center gap-1 text-[11px] font-medium bg-stone-100 text-stone-600 px-2 py-0.5 rounded-full" data-testid="tfa-badge-off">Off</span>
              )}
              {required_by_role && (
                <span className="inline-flex items-center gap-1 text-[11px] font-medium bg-amber-100 text-amber-700 px-2 py-0.5 rounded-full">
                  <AlertTriangle className="h-3 w-3" />Required for your role
                </span>
              )}
            </div>
            <p className="text-sm text-stone-500 leading-relaxed">
              {enabled ? (
                <>Extra security: we'll email a 6-digit code whenever you sign in from a new session. {backup_codes_remaining} backup code{backup_codes_remaining === 1 ? '' : 's'} remaining.</>
              ) : (
                <>Adds an email-verification step every time you sign in. Strongly recommended, especially for accounts that receive payouts.</>
              )}
            </p>

            <div className="flex flex-wrap gap-2 mt-4">
              {enabled ? (
                <>
                  <Button variant="outline" onClick={startRegenerate} disabled={submitting} className="gap-1" data-testid="tfa-regen-btn">
                    <RefreshCw className="h-4 w-4" />Regenerate backup codes
                  </Button>
                  {!required_by_role && (
                    <Button variant="outline" onClick={startDisable} disabled={submitting} className="border-red-200 text-red-600 hover:bg-red-50 gap-1" data-testid="tfa-disable-btn">
                      <ShieldOff className="h-4 w-4" />Disable
                    </Button>
                  )}
                </>
              ) : (
                <Button onClick={startEnable} disabled={submitting} className="bg-[#D4AF37] hover:bg-[#C49E2F] text-white gap-1" data-testid="tfa-enable-btn">
                  {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : <ShieldCheck className="h-4 w-4" />}
                  Enable 2FA
                </Button>
              )}
            </div>

            {backup_codes_remaining === 0 && enabled && (
              <div className="mt-3 rounded-lg bg-amber-50 border border-amber-200 px-3 py-2 text-xs text-amber-700 flex items-start gap-2">
                <AlertTriangle className="h-4 w-4 shrink-0 mt-0.5" />
                You've used all your backup codes. Regenerate a fresh set before you get locked out.
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Enable, confirmation OTP */}
      <AlertDialog open={action === 'enable'} onOpenChange={(v) => !v && setAction(null)}>
        <AlertDialogContent data-testid="tfa-enable-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Verify it's you</AlertDialogTitle>
            <AlertDialogDescription>Enter the 6-digit code we just emailed you.</AlertDialogDescription>
          </AlertDialogHeader>
          <Input
            inputMode="numeric" maxLength={6} placeholder="123456"
            value={setupCode}
            onChange={(e) => setSetupCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
            className="text-center text-xl tracking-[0.4em] font-mono"
            data-testid="tfa-setup-code-input"
          />
          <AlertDialogFooter>
            <AlertDialogCancel onClick={() => { setSetupCode(''); setSetupToken(null); }}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={confirmEnable}
              disabled={submitting || setupCode.length !== 6}
              className="bg-[#D4AF37] hover:bg-[#C49E2F]"
              data-testid="tfa-confirm-enable"
            >
              {submitting ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : null}
              Enable 2FA
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Disable, confirmation OTP */}
      <AlertDialog open={action === 'disable'} onOpenChange={(v) => !v && setAction(null)}>
        <AlertDialogContent data-testid="tfa-disable-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-red-600">Disable two-factor authentication?</AlertDialogTitle>
            <AlertDialogDescription>Enter the 6-digit code we just emailed you to confirm.</AlertDialogDescription>
          </AlertDialogHeader>
          <Input
            inputMode="numeric" maxLength={6} placeholder="123456"
            value={disableCode}
            onChange={(e) => setDisableCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
            className="text-center text-xl tracking-[0.4em] font-mono"
            data-testid="tfa-disable-code-input"
          />
          <AlertDialogFooter>
            <AlertDialogCancel onClick={() => setDisableCode('')}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={confirmDisable}
              disabled={submitting || disableCode.length !== 6}
              className="bg-red-500 hover:bg-red-600"
              data-testid="tfa-confirm-disable"
            >Disable</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Regenerate, confirmation OTP */}
      <AlertDialog open={action === 'regenerate'} onOpenChange={(v) => !v && setAction(null)}>
        <AlertDialogContent data-testid="tfa-regen-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Regenerate backup codes</AlertDialogTitle>
            <AlertDialogDescription>Existing codes will be invalidated. Enter the 6-digit code we just emailed you.</AlertDialogDescription>
          </AlertDialogHeader>
          <Input
            inputMode="numeric" maxLength={6} placeholder="123456"
            value={setupCode}
            onChange={(e) => setSetupCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
            className="text-center text-xl tracking-[0.4em] font-mono"
            data-testid="tfa-regen-code-input"
          />
          <AlertDialogFooter>
            <AlertDialogCancel onClick={() => setSetupCode('')}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={confirmRegenerate}
              disabled={submitting || setupCode.length !== 6}
              data-testid="tfa-confirm-regen"
            >Regenerate</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {newCodes && (
        <BackupCodesView codes={newCodes} onClose={() => setNewCodes(null)} />
      )}
    </>
  );
}
